import { useContext, useMemo } from 'react';

import {
  ClosedEyeIcon,
  DefaultImageIcon,
  OpenedEyeIcon,
  TelegramBellIcon,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Column } from 'react-table';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { TGood } from 'types';

import s from './GoodsGoodTable.module.scss';
import { IUseColumns } from './interfaces';

export const useColumns = ({
  toggleShowInShell,
  updateGoods,
}: IUseColumns): Column<TGood>[] => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency, lowStockThreshold, telegramChannelId },
    },
  } = useContext(SettingsContext);

  return useMemo(
    () => [
      {
        Header: '',
        id: 'image',
        accessor: ({ image }) => (!image ? false : image),
        Cell: ({ value }: { value: string }) => {
          return (
            <div className={s.column_img_block}>
              {value ? (
                <Tooltip
                  className={s.column_img_tooltip}
                  contentClassName={s.column_img_content}
                  content={<img alt="food-icon" src={value} />}
                  position={'bottom-left'}
                >
                  <img alt="food-icon" className={s.column_img} src={value} />
                </Tooltip>
              ) : (
                <DefaultImageIcon color={'secondary'} />
              )}
            </div>
          );
        },
        width: 80,
      },
      {
        Header: t('title'),
        accessor: 'title',
        width: 330,
      },
      {
        Header: t('group'),
        id: 'category',
        accessor: ({ category }) => (category?.title ? category.title : false),
        Cell: ({ value }: { value: string }) => {
          return (
            <div
              className={cn(s.column_category_block, value && s.active_block)}
            >
              <span className={cn(s.text, value && s.active)}>
                {value ? value : t('goods.table.category_empty')}
              </span>
            </div>
          );
        },
        width: 232,
      },
      {
        Header: t('discount'),
        id: 'use_global_discounts',
        accessor: ({ use_global_discounts }) => use_global_discounts || false,
        Cell: ({ value }: { value: string }) => {
          return (
            <div
              className={cn(s.column_category_block, value && s.active_block)}
            >
              <span className={cn(s.text, value && s.active)}>
                {value ? t('discount.applies') : t('discount.applies.no')}
              </span>
            </div>
          );
        },
        width: 145,
        sortType: (rowA, rowB) => {
          const a = rowA.values.use_global_discounts;
          const b = rowB.values.use_global_discounts;
          return a === b ? 0 : a ? -1 : 1;
        },
      },
      {
        Header: t('host.seance.rest'),
        accessor: 'amount',
        Cell: ({ value, row }) => {
          return (
            <>
              {telegramChannelId?.id &&
              row?.original?.low_stock_notification?.enabled &&
              row?.original?.low_stock_notification?.threshold ? (
                <Tooltip
                  contentClassName={s.seance_tooltip}
                  header={t('goodsTable.seance.tooltip.title')}
                  content={t(
                    'goodsTable.seance.tooltip.content',
                    row.original.low_stock_notification?.threshold
                      ? row.original.low_stock_notification?.threshold
                      : lowStockThreshold,
                    t('pcs')
                  )}
                >
                  <div className={s.seance}>
                    <TelegramBellIcon
                      className={s.seance_icon}
                      color="link-info"
                    />
                    <span
                      className={cn(
                        s.seance_text,
                        row.original.amount <=
                          row?.original?.low_stock_notification?.threshold &&
                          s.seance_text_active
                      )}
                    >
                      {`${value.toLocaleString()} ${t('pcs')}`}
                    </span>
                  </div>
                </Tooltip>
              ) : (
                <div className={s.seance}>
                  <span className={cn(s.seance_text)}>
                    {`${value.toLocaleString()} ${t('pcs')}`}
                  </span>
                </div>
              )}
            </>
          );
        },
        width: 160,
      },
      {
        Header: t('good.price.buy'),
        accessor: 'wholesale_cost',
        Cell: ({ value }) => `${value.toLocaleString()} ${currency.sign}`,
        width: 160,
      },
      {
        Header: t('good.price.sell'),
        accessor: 'cost',
        Cell: ({ value }) => `${value.toLocaleString()} ${currency.sign}`,
        width: 160,
      },
      {
        Header: t('good.price.rest'),
        accessor: (row) => row.cost * row.amount,
        Cell: ({ value }: { value: string }) =>
          `${value.toLocaleString()} ${currency.sign}`,
        width: 189,
      },
      {
        Header: t('good.table.show_in_shell.title'),
        accessor: 'show_in_shell',
        width: 160,
        Cell: ({ row, value }) => {
          return (
            <div>
              <Tooltip
                position={'bottom-right'}
                className={s.tooltip}
                contentClassName={s.tooltip_content}
                content={
                  <span style={{ fontSize: '12px', lineHeight: '16px' }}>
                    {t('group.table.show_in_shell.title')}
                  </span>
                }
              >
                <div
                  onClick={() => {
                    if (row?.original?.in_combo) {
                      return updateGoods({
                        ids: [row?.original?.id],
                        input: { show_in_shell: !row?.original?.show_in_shell },
                      });
                    }

                    toggleShowInShell({
                      ...row.original,
                      show_in_shell: !row?.original?.show_in_shell,
                    });
                  }}
                  className={cn(s.show_icon, value && s.active)}
                >
                  {value ? (
                    <OpenedEyeIcon size={'s'} />
                  ) : (
                    <ClosedEyeIcon color={'secondary'} size={'s'} />
                  )}
                </div>
              </Tooltip>
            </div>
          );
        },
        sortType: (rowA, rowB) => {
          const a = rowA.values.show_in_shell;
          const b = rowB.values.show_in_shell;
          return a === b ? 0 : a ? -1 : 1;
        },
      },
    ],
    [t, telegramChannelId]
  );
};
